/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 */
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {ArrowRightIcon} from '@amblique/king-living/app/components/icons';
import {
  Box,
  Button,
  Flex,
  Image,
  Skeleton,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

const SearchTypeaheadSkeleton = ({marginTop, styles, variant}) => (
  <Box data-testid="search-typeahead-loading" mt={marginTop}>
    <Box {...styles.section}>
      <Skeleton height={{base: '10px', md: '12px'}} width="140px" />
      <Box {...styles.list}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            height={
              variant === 'drawer' ?
                {base: '56px', md: '56px', lg: '72px'} :
                {base: '80px', md: '75px'}
            }
            width="full"
          />
        ))}
      </Box>
    </Box>
  </Box>
);

SearchTypeaheadSkeleton.propTypes = {
  marginTop: PropTypes.number,
  styles: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['drawer']),
};

/**
 * Search typeahead: loading skeleton, results heading + rows, empty state, optional View all.
 */
const SearchTypeaheadPanel = ({
  liveQueryTrimmed,
  debouncedQueryTrimmed,
  minQueryLength,
  isPanelLoading,
  hasDisplayableResults,
  displayRows,
  marginTop = '32px',
  viewAllProductTotal,
  visibleProductRowCount,
  onViewAllResults,
  variant,
}) => {
  const styles = useMultiStyleConfig('SearchTypeaheadPanel', {variant});

  const showViewAll = useMemo(() => {
    if (displayRows.length < 1) {
      return false;
    }

    if (typeof viewAllProductTotal !== 'number' || viewAllProductTotal < 1) {
      return false;
    }

    return variant === 'drawer' || viewAllProductTotal > visibleProductRowCount;
  }, [displayRows.length, variant, viewAllProductTotal, visibleProductRowCount]);

  if (!liveQueryTrimmed || liveQueryTrimmed.length < minQueryLength) {
    return null;
  }

  if (isPanelLoading) {
    return (
      <SearchTypeaheadSkeleton marginTop={marginTop} styles={styles} variant={variant} />
    );
  }

  const settled =
    debouncedQueryTrimmed === liveQueryTrimmed &&
    debouncedQueryTrimmed.length >= minQueryLength;

  if (!settled) {
    return (
      <Box data-testid="search-typeahead-debouncing" mt={marginTop}>
        <Box {...styles.section}>
          <Skeleton height={{base: '10px', md: '12px'}} width="140px" />
          <Box {...styles.list}>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                height={
              variant === 'drawer' ?
                {base: '56px', md: '56px', lg: '72px'} :
                {base: '80px', md: '75px'}
            }
                width="full"
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  if (!hasDisplayableResults) {
    return (
      <Box
        data-testid="search-typeahead-empty"
        mt={variant === 'drawer' ? 0 : {base: '24px', md: '32px'}}
      >
        <Text color="brandColors.primaryText" fontSize={{base: 'md', md: 'lg'}}>
          <FormattedMessage
            defaultMessage="Sorry, no matches were found for {quotedQuery}"
            id="search.results.none.line1"
            values={{
              quotedQuery: `'${debouncedQueryTrimmed}'`,
            }}
          />
        </Text>
        <Text color="brandColors.primaryText" fontSize={{base: 'md', md: 'lg'}} mt={2}>
          <FormattedMessage
            defaultMessage="Please try a new search or use suggestions."
            id="search.results.none.line2"
          />
        </Text>
      </Box>
    );
  }

  return (
    <Box data-testid="search-typeahead-results" mt={marginTop} {...styles.wrap}>
      <Box {...styles.section}>
        <Text {...styles.heading}>
          {variant === 'drawer' ? (
            <FormattedMessage
              defaultMessage="Results"
              id="search.results.drawer_heading"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Search results"
              id="search.results.heading"
            />
          )}
        </Text>
        <Box {...styles.list}>
          {displayRows.map((row) => {
            const rowProps = {
              key: row.key,
              ...styles.item,
              onMouseDown: () => row.onSelect(),
            };

            const rowInner = (
              <Flex {...styles.itemInner}>
                <Box {...styles.itemImage}>
                  {row.imageUrl ? (
                    <Image
                      alt=""
                      h={variant === 'drawer' ? {base: '56px', md: '56px', lg: '72px'} : 'full'}
                      maxW={variant === 'drawer' ? 'none' : undefined}
                      objectFit="cover"
                      objectPosition="center"
                      src={row.imageUrl}
                      w={variant === 'drawer' ? {base: '75px', md: '75px', lg: '96px'} : 'full'}
                    />
                  ) : null}
                </Box>
                <Flex {...styles.itemInfo}>
                  <Text isTruncated={variant === 'drawer'} {...styles.itemTitle}>
                    <span dangerouslySetInnerHTML={{__html: row.titleHtml}} />
                  </Text>
                  <Text {...styles.itemTag} noOfLines={1}>
                    {row.tag}
                  </Text>
                </Flex>
              </Flex>
            );

            if (variant === 'drawer') {
              return (
                <Box as="button" type="button" {...rowProps}>
                  {rowInner}
                </Box>
              );
            }

            return (
              <Button variant="unstyled" {...rowProps}>
                {rowInner}
              </Button>
            );
          })}
        </Box>
      </Box>
      {showViewAll ? (
        <Flex
          as="span"
          role="button"
          tabIndex={0}
          {...styles.viewAll}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onViewAllResults(e);
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            onViewAllResults(e);
          }}
        >
          {variant === 'drawer' ? (
            <Text as="span">
              <FormattedMessage
                defaultMessage="View all {count} results"
                id="search.results.view_all_count"
                values={{count: viewAllProductTotal}}
              />
            </Text>
          ) : (
            <FormattedMessage
              defaultMessage="View all {count} results"
              id="search.results.view_all_count"
              values={{count: viewAllProductTotal}}
            />
          )}
          {variant === 'drawer' ? (
            <ArrowRightIcon aria-hidden {...styles.viewAllIcon} />
          ) : null}
        </Flex>
      ) : null}
    </Box>
  );
};

SearchTypeaheadPanel.propTypes = {
  liveQueryTrimmed: PropTypes.string.isRequired,
  debouncedQueryTrimmed: PropTypes.string.isRequired,
  minQueryLength: PropTypes.number.isRequired,
  isPanelLoading: PropTypes.bool.isRequired,
  hasDisplayableResults: PropTypes.bool.isRequired,
  marginTop: PropTypes.number,
  displayRows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      kind: PropTypes.oneOf(['product', 'article', 'blog']).isRequired,
      titleHtml: PropTypes.string.isRequired,
      tag: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      onSelect: PropTypes.func.isRequired,
    })
  ).isRequired,
  viewAllProductTotal: PropTypes.number,
  visibleProductRowCount: PropTypes.number.isRequired,
  onViewAllResults: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['drawer']),
};

export default SearchTypeaheadPanel;
